<!--
 * A Vue component that wraps the b-form-input element of Bootstrap Vue.
 * This element is required by default.
 *
 *
 * Properties
 * ==========
 * - name -
 * (Optional) The text that is used as id and name for input element.
 *
 * - description -
 * (Optional) A description that appears below the input element.
 *
 * - text -
 * (Optional) The text to show on the label above the input field.
 *
 * - state -
 * (Optional) The validation state to set from parent component. state === true => Valid state
 *                                                               state === false => Invalid state
 *
 * - invalidMessage -
 * (Optional) The text to show when state is set to false (invalid).
 *
 * - labelSize -
 * (Optional) To control the label text size. Valid values are 'lg' for large and 'sm' for small.
 *
 * - value -
 * (Optional) To set the initial value of the input element.
 *
 * - listValues -
 * (Required) List with values to display in popup list
 *
 * - simpleMode -
 * (Optional) Shows the red border based on the state property.
 * Does not display green border.
 *
 *
 * Events
 * ==========
 * - input ; blur; change-
 * This event is sent when user changes the input value.
 * The object to sent along with this event is the inserted value from input element.
 *
 * - blur and change -
 * This event is sent when user changes the input value and leaves the input.
 * The object to sent along with this event is the inserted value from input element.
 *
 * Usage
 * ==========
 * -You do not need to listen to the emitted input event from this component.
 * Just use the v-model in your parent component and the value will be updated
 * from this component automatically.
 *
 *-You can put the standard html input attributes, sucht as required,
 * autofocus, pattern, etc. directly from the parent component without explicit binding.
 * The custom attributes like listed above, should be bind.
-->

<template>
    <div>
        <b-form-group
            id="fieldset-1"
            :description="description"
            :label="text"
            :label-for="name"
            :label-class="labelClass"
            :label-size="labelSize"
            :invalid-feedback="invalidFeedback"
            :state="stateValide"
        >
            <b-form-input
                v-bind="$attrs"
                :id="name"
                :name="name"
                :value="value"
                :size="labelSize"
                :state="stateValide"
                :disabled="disabled"
                :required="required"
                :list="`x-datalist-${name}`"
                @input="_onInput"
                @blur="_onBlur"
                @change="_onChange"
                trim
                autocomplete="off"
            >
            </b-form-input>
            <datalist :id="`x-datalist-${name}`">
                <option v-for="item in options" v-bind:key="item.value">
                    {{ item.text }}
                </option>
            </datalist>
        </b-form-group>
    </div>
</template>

<script>
    export default {
        inheritAttrs: false,
        name: 'x-datalist',
        props: {
            description: { type: String, required: false },
            name: { type: String, required: false, default: 'bv-datalist' },
            text: { type: String, required: false, default: '' },
            state: { type: Boolean, required: false, default: true },
            invalidMessage: { type: String, required: false, default: '' },
            labelSize: { type: String, required: false, default: '' },
            disabled: { type: Boolean, required: false, default: false },
            required: { type: Boolean, required: false, default: false },
            options: { type: Array, required: true },
            value: [String, Number],
            simpleMode: { type: Boolean, required: false, default: false },
            labelClass: { type: String, default: 'font-weight-normal' }
        },

        data() {
            return { inputDirty: false };
        },

        computed: {
            stateValide() {
                let calculatedState = null;

                if (this.simpleMode) {
                    calculatedState = this.state ? null : this.state;
                } else if (this.inputDirty && this.value) {
                    calculatedState = this.value.length > 0 && this.state;
                } else {
                    calculatedState = !this.required;
                }
                return calculatedState;
            },

            invalidFeedback() {
                if (this.value && this.value.length > 0 && !this.state) {
                    return this.invalidMessage;
                } else if (this.value && this.value.length === 0) {
                    return this.$t('requiredField');
                } else {
                    return '';
                }
            }
        },

        methods: {
            _onBlur: function (input) {
                this.inputDirty = true;
                this.$emit('blur', input);
            },

            _onChange: function (input) {
                this.inputDirty = true;
                this.$emit('change', input);
            },

            _onInput: function (input) {
                this.inputDirty = true;
                this.$emit('input', input);
            }
        }
    };
</script>
